import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import Popover from "@mui/material/Popover";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";

/**
 * Orange "pending call" badge shown on an unlocked product card's info-icon
 * slot when that product has an active pending call. Clicking it opens a
 * small popover (Figma node 8828:11010) with a CTA to that product's Action
 * Centre, without triggering the card's own onClick (view dashboard).
 */
const PendingActionBadge = ({ actionCentrePath, pendingCount = 1 }) => {
  const history = useHistory();
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    event?.stopPropagation();
    setAnchorEl(null);
  };

  const handleGoToActionCentre = (event) => {
    event.stopPropagation();
    setAnchorEl(null);
    if (actionCentrePath) {
      history.push(actionCentrePath);
    }
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={handleOpen}
        aria-label="Pending action"
        sx={{
          width: 32,
          height: 32,
          p: 0,
          border: 0,
          borderRadius: "999px",
          cursor: "pointer",
          flexShrink: 0,
          lineHeight: 0,
        }}
      >
        <Box
          component="img"
          src="/images/product-info/pending-call-badge.svg"
          alt=""
          sx={{ width: "100%", height: "100%", display: "block" }}
        />
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        onClick={(event) => event.stopPropagation()}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 264,
              borderRadius: "24px",
              border: "1px solid #E5E7EB",
              background: "linear-gradient(180deg, #FFFFFF 0%, #F9FAFB 100%)",
              boxShadow:
                "0px 18px 24px rgba(0,0,0,0.07), 0px 2px 5px rgba(0,0,0,0.04)",
              p: "24px 20px 20px",
            },
          },
        }}
      >
        <Stack spacing={2}>
          <Text sx={{ fontSize: 16, fontWeight: 600, color: "#1F2937" }}>
            Pending Actionables{" "}
            <Text component="span" sx={{ fontWeight: 700, color: "#2563EB" }}>
              ({pendingCount})
            </Text>
          </Text>
          <Box sx={{ borderTop: "1px solid #E5E7EB" }} />
          <Stack spacing={1}>
            <Box
              component="button"
              type="button"
              onClick={handleGoToActionCentre}
              sx={{
                width: "100%",
                border: 0,
                borderRadius: "12px",
                bgcolor: "#2563EB",
                boxShadow: "0px 4px 6px rgba(37,99,235,0.17)",
                py: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                cursor: "pointer",
              }}
            >
              <Text sx={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>
                Go to Action Center
              </Text>
              <Box
                component="img"
                src="/images/product-info/pending-popup-arrow.svg"
                alt=""
                sx={{ width: 12, height: 12 }}
              />
            </Box>
            <Text
              sx={{
                fontSize: 10,
                color: "#9CA3AF",
                textAlign: "center",
              }}
            >
              Requires immediate review to optimize portfolio
            </Text>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};

export default PendingActionBadge;
